// src/components/GameList.tsx - Updated with deck selection for accepting invites

import React, { useState } from 'react';
import { Game, DeckChoice } from '../types';
import DeckSelector from './DeckSelector';
import { ddApi } from '../lib/supabaseClient';


interface GameListProps {
  games: Game[];
  currentUserId: string;
  onJoinGame: (gameId: number) => void;
  onAcceptGame: (gameId: number, deckChoice: DeckChoice) => void;
  onRefresh?: () => void;
  needsStarterPack?: boolean;
  onGetStarterPack?: () => void;
  gettingStarterPack?: boolean;
  starterPackMessage?: {
    type: 'success' | 'error';
    text: string;
  } | null;
}

interface GameSection {
  title: string;
  games: Game[];
  priority: number;
  emptyMessage?: string;
}


const GameList: React.FC<GameListProps> = ({ 
  games, 
  currentUserId, 
  onJoinGame, 
  onAcceptGame, 
  onRefresh,
    needsStarterPack = false,
  onGetStarterPack,
  gettingStarterPack = false,
  starterPackMessage
}) => {
	  const [refreshing, setRefreshing] = useState(false);
  const [acceptingGame, setAcceptingGame] = useState<number | null>(null);
  const [showDeckSelector, setShowDeckSelector] = useState<number | null>(null);
  const [selectedDeck, setSelectedDeck] = useState<DeckChoice>('deck_1');

const handleRefresh = async () => {
    if (!onRefresh || refreshing) return;
    
    setRefreshing(true);
    try {
      await onRefresh();
    } finally {
      setRefreshing(false);
    }
  };

  const getGameStatus = (game: Game) => {
    const isPlayer1 = game.player1_id === currentUserId;
    const isMyTurn = (game.current_turn === 1 && isPlayer1) || 
                     (game.current_turn === 2 && !isPlayer1);

    switch (game.game_status) {
      case 'pending':
        return game.player1_id === currentUserId 
          ? { text: 'Waiting for response', color: 'text-yellow-400', action: null }
          : { text: 'Invitation received', color: 'text-green-400', action: 'accept' };
      case 'active':
        return isMyTurn
          ? { text: 'Your turn', color: 'text-blue-400', action: 'play' }
          : { text: 'Opponent\'s turn', color: 'text-gray-400', action: 'wait' };
      case 'completed':
        const wonGame = game.winner_id === currentUserId;
        return { 
          text: wonGame ? 'Victory!' : 'Defeat', 
          color: wonGame ? 'text-green-400' : 'text-red-400', 
          action: 'view' 
        };
      default:
        return { text: 'Unknown', color: 'text-gray-400', action: null };
    }
  };

  const handleAcceptClick = (gameId: number) => {
    setShowDeckSelector(gameId);
    setSelectedDeck('deck_1'); // Reset to default
  };

  const handleDeckSelect = (deckChoice: DeckChoice) => {
    setSelectedDeck(deckChoice);
  };

  const handleConfirmAccept = async (gameId: number) => {
    setAcceptingGame(gameId);
    setShowDeckSelector(null);
    
    try {
      await onAcceptGame(gameId, selectedDeck);
    } finally {
      setAcceptingGame(null);
    }
  };

  const handleCancelAccept = () => {
    setShowDeckSelector(null);
    setSelectedDeck('deck_1');
  };

  // Organize games into logical sections
  const organizeGames = (): GameSection[] => {
    const activeMyTurn: Game[] = [];
    const activeOpponentTurn: Game[] = [];
    const pendingInvitations: Game[] = [];
    const pendingSent: Game[] = [];
    const completedGames: Game[] = [];

    games.forEach(game => {
      const isPlayer1 = game.player1_id === currentUserId;
      const isMyTurn = (game.current_turn === 1 && isPlayer1) || 
                       (game.current_turn === 2 && !isPlayer1);

      switch (game.game_status) {
        case 'pending':
          if (game.player1_id === currentUserId) {
            pendingSent.push(game);
          } else {
            pendingInvitations.push(game);
          }
          break;
        case 'active':
          if (isMyTurn) {
            activeMyTurn.push(game);
          } else {
            activeOpponentTurn.push(game);
          }
          break;
        case 'completed':
          completedGames.push(game);
          break;
      }
    });

    // Sort each section appropriately
    activeMyTurn.sort((a, b) => new Date(a.last_activity).getTime() - new Date(b.last_activity).getTime());
    activeOpponentTurn.sort((a, b) => new Date(b.last_activity).getTime() - new Date(a.last_activity).getTime());
    pendingInvitations.sort((a, b) => new Date(a.invited_at).getTime() - new Date(b.invited_at).getTime());
    pendingSent.sort((a, b) => new Date(b.invited_at).getTime() - new Date(a.invited_at).getTime());
    completedGames.sort((a, b) => new Date(b.last_activity).getTime() - new Date(a.last_activity).getTime());

    const sections: GameSection[] = [];

    if (activeMyTurn.length > 0) {
      sections.push({
        title: '🎯 Your Turn',
        games: activeMyTurn,
        priority: 1
      });
    }

    if (activeOpponentTurn.length > 0) {
      sections.push({
        title: '⏳ Waiting for Opponent',
        games: activeOpponentTurn,
        priority: 2
      });
    }

    if (pendingInvitations.length > 0) {
      sections.push({
        title: '📨 Invitations Received',
        games: pendingInvitations,
        priority: 3
      });
    }

    if (pendingSent.length > 0) {
      sections.push({
        title: '📤 Invitations Sent',
        games: pendingSent,
        priority: 4
      });
    }

    if (completedGames.length > 0) {
      sections.push({
        title: '✅ Recent Games',
        games: completedGames.slice(0, 10),
        priority: 5
      });
    }

    return sections;
  };

  const formatTimeAgo = (timestamp: string) => {
    const diff = Date.now() - new Date(timestamp).getTime();
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);

    if (days > 0) return `${days}d ago`;
    if (hours > 0) return `${hours}h ago`;
    if (minutes > 0) return `${minutes}m ago`;
    return 'Just now';
  };

  const getOpponentInfo = (game: Game) => {
    const isPlayer1 = game.player1_id === currentUserId;
    return {
      name: isPlayer1 ? game.player2_username : game.player1_username,
      faction: isPlayer1 ? game.player2_faction : game.player1_faction,
      health: isPlayer1 ? game.player2_health : game.player1_health,
    };
  };

  const getPlayerInfo = (game: Game) => {
    const isPlayer1 = game.player1_id === currentUserId;
    return {
      health: isPlayer1 ? game.player1_health : game.player2_health,
    };
  };

  const renderGameCard = (game: Game) => {
    const status = getGameStatus(game);
    const opponent = getOpponentInfo(game);
    const player = getPlayerInfo(game);

    return (
      <div 
        key={game.game_id}
        className="bg-gray-800 rounded-lg border border-gray-700 p-4 hover:bg-gray-750 transition-colors"
      >
        <div className="flex items-center justify-between">
          {/* Game Info */}
          <div className="flex-1">
            <div className="flex items-center space-x-4">
              <div>
                <h3 className="font-semibold text-white">
                  vs {opponent.name}
                  <span className={`ml-2 text-sm ${
                    opponent.faction === 1 ? 'text-blue-400' : 'text-red-400'
                  }`}>
                    ({opponent.faction === 1 ? 'Defender' : 'Attacker'})
                  </span>
                </h3>
                <p className="text-sm text-gray-400">
                  Turn {game.turn_number} • {formatTimeAgo(game.last_activity)}
                </p>
                
                {/* Show deck choice if available */}
                {game.player1_deck_choice && game.player2_deck_choice && (
                  <p className="text-xs text-purple-400 mt-1">
                    🃏 Using: {game.player1_id === currentUserId ? game.player1_deck_choice : game.player2_deck_choice}
                  </p>
                )}
              </div>
            </div>
            
            {/* Health bars */}
            {game.game_status === 'active' && (
              <div className="mt-3 flex items-center space-x-4 text-sm">
                <div className="flex items-center space-x-2">
                  <span className="text-green-400">You:</span>
                  <div className="w-24 bg-gray-600 rounded-full h-2">
                    <div 
                      className="bg-green-500 h-2 rounded-full transition-all"
                      style={{ width: `${(player.health / 20) * 100}%` }}
                    />
                  </div>
                  <span className="text-white font-mono">{player.health}/20</span>
                </div>
                
                <div className="flex items-center space-x-2">
                  <span className="text-red-400">Opponent:</span>
                  <div className="w-24 bg-gray-600 rounded-full h-2">
                    <div 
                      className="bg-red-500 h-2 rounded-full transition-all"
                      style={{ width: `${(opponent.health / 20) * 100}%` }}
                    />
                  </div>
                  <span className="text-white font-mono">{opponent.health}/20</span>
                </div>
              </div>
            )}
          </div>

          {/* Status and Actions */}
          <div className="text-right">
            <p className={`font-medium ${status.color}`}>
              {status.text}
            </p>
            
            <div className="mt-2 space-x-2">
              {status.action === 'accept' && (
                <button
                  onClick={() => handleAcceptClick(game.game_id)}
                  disabled={acceptingGame === game.game_id || showDeckSelector !== null}
                  className="bg-green-600 hover:bg-green-700 disabled:bg-gray-600 text-white px-4 py-2 rounded text-sm font-medium transition-colors"
                >
                  {acceptingGame === game.game_id ? (
                    <span className="flex items-center justify-center">
                      <svg className="animate-spin -ml-1 mr-3 h-4 w-4" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Accepting...
                    </span>
                  ) : (
                    'Accept'
                  )}
                </button>
              )}
              
              {(status.action === 'play' || status.action === 'wait' || status.action === 'view') && (
                <button
                  onClick={() => onJoinGame(game.game_id)}
                  className={`px-4 py-2 rounded text-sm font-medium transition-colors ${
                    status.action === 'play'
                      ? 'bg-blue-600 hover:bg-blue-700 text-white'
                      : 'bg-gray-600 hover:bg-gray-700 text-white'
                  }`}
                >
                  {status.action === 'play' ? 'Play Turn' : 
                   status.action === 'wait' ? 'View Game' : 'View Results'}
                </button>
              )}
			                {(game.game_status === 'pending' || game.game_status === 'active') && 
               isGameStale(game.last_activity) && (
                <button
onClick={() => handleAbandonGame(game.game_id, opponent.name || 'Unknown Player')}
                  className="bg-red-600 hover:bg-red-700 text-white px-3 py-1 rounded text-sm font-medium transition-colors"
                  title={`Last activity: ${formatTimeAgo(game.last_activity)}`}
                >
                  🗑️ Abandon
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

// Add this function inside your GameList component
const handleAbandonGame = async (gameId: number, gameName: string) => {
  if (window.confirm(`Are you sure you want to abandon this game against ${gameName}? This cannot be undone.`)) {
    try {
      const result = await ddApi.abandonGame(gameId, currentUserId);
      
      if (result.success) {
        if (onRefresh) onRefresh(); // Refresh the game list
      } else {
        alert(result.error || 'Failed to abandon game');
      }
    } catch (error) {
      console.error('Error abandoning game:', error);
      alert('Error abandoning game');
    }
  }
};

// Add this helper function to check if game is stale
const isGameStale = (lastActivity: string): boolean => {
  const sevenDaysAgo = new Date();
  sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 3);
  return new Date(lastActivity) < sevenDaysAgo;
};

  const sections = organizeGames();

  if (games.length === 0) {
    return (
      <div className="text-center py-12">
        <div className="text-gray-400 mb-4">
          <svg className="w-16 h-16 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
          </svg>
        </div>
        <h3 className="text-xl font-medium text-white mb-2">No active games</h3>
        <p className="text-gray-400">Challenge other players to start your first Digital Duel!</p>
      </div>
    );
  }

  return (
    <div>
      {/* Deck Selection Modal */}
      {showDeckSelector && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-gray-800 rounded-lg p-6 max-w-lg w-full mx-4 max-h-[80vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold text-white">
                Accept Game Invitation
              </h3>
              <button
                onClick={handleCancelAccept}
                className="text-gray-400 hover:text-white"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <DeckSelector
              onDeckSelect={handleDeckSelect}
              selectedDeck={selectedDeck}
              title="Choose your deck for this game"
            />

            <div className="flex space-x-3 mt-6">
              <button
                onClick={() => handleConfirmAccept(showDeckSelector)}
                disabled={acceptingGame === showDeckSelector}
                className="flex-1 bg-green-600 hover:bg-green-700 disabled:bg-gray-600 text-white px-4 py-2 rounded font-medium transition-colors"
              >
                {acceptingGame === showDeckSelector ? (
                  <span className="flex items-center justify-center">
                    <svg className="animate-spin -ml-1 mr-3 h-4 w-4" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Accepting...
                  </span>
                ) : (
                  'Accept Game'
                )}
              </button>
              <button
                onClick={handleCancelAccept}
                className="px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded font-medium transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

 <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold">My Games</h2>
        
        <div className="flex items-center gap-4">
          {/* Starter Pack Button or Game Count */}
          {needsStarterPack && onGetStarterPack ? (
            <div className="flex flex-col items-end gap-2">
              <button
                onClick={onGetStarterPack}
                disabled={gettingStarterPack}
                className="bg-gradient-to-r from-green-600 to-green-500 hover:from-green-700 hover:to-green-600 disabled:from-gray-600 disabled:to-gray-700 text-white px-6 py-3 rounded-lg font-bold text-lg shadow-lg transform transition-all duration-200 hover:scale-105 disabled:scale-100 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {gettingStarterPack ? (
                  <>
                    <svg className="animate-spin h-5 w-5" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Getting Cards...
                  </>
                ) : (
                  <>
                    🎁 Get My Starter Pack
                  </>
                )}
              </button>
              
              {/* Show message if exists */}
              {starterPackMessage && (
                <div className={`text-sm px-3 py-1 rounded animate-pulse ${
                  starterPackMessage.type === 'success' 
                    ? 'bg-green-600 text-white' 
                    : 'bg-red-600 text-white'
                }`}>
                  {starterPackMessage.text}
                </div>
              )}
            </div>
          ) : (
            <div className="text-sm text-gray-400">
              {games.length} total games
            </div>
          )}

          {/* Refresh Button */}
          {onRefresh && (
            <button
              onClick={handleRefresh}
              disabled={refreshing}
              className="bg-blue-600 hover:bg-blue-700 disabled:bg-gray-600 text-white px-3 py-2 rounded text-sm font-medium transition-colors flex items-center"
            >
              <svg 
                className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} 
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              {refreshing ? 'Refreshing...' : 'Refresh'}
            </button>
          )}
        </div>
      </div>
      
      <div className="space-y-8">
        {sections.map((section, sectionIndex) => (
          <div key={sectionIndex}>
            {/* Section Header */}
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-white flex items-center">
                {section.title}
                <span className="ml-2 bg-gray-600 text-xs px-2 py-1 rounded-full">
                  {section.games.length}
                </span>
              </h3>
              
              {/* Priority indicator for urgent sections */}
              {section.priority <= 2 && section.games.length > 0 && (
                <div className={`text-xs px-2 py-1 rounded ${
                  section.priority === 1 
                    ? 'bg-blue-600 text-white' 
                    : 'bg-yellow-600 text-black'
                }`}>
                  {section.priority === 1 ? 'ACTION NEEDED' : 'WAITING'}
                </div>
              )}
            </div>
            
            {/* Games in this section */}
            <div className="space-y-4">
              {section.games.map(game => renderGameCard(game))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default GameList;